import {
  Box,
  Card,
  TextField,
  MenuItem,
  FormControl,
  Select,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { useLocation, useNavigate } from "react-router-dom";
import api from "api/api";

const GROUP_OPTIONS = [
  { value: "xp_triggers", label: "XP Triggers" },
  { value: "caps", label: "Caps" },
  { value: "session_rules", label: "Session Rules" },
  { value: "streak_rules", label: "Streak Rules" },
];

const toHumanReadable = (value) => {
  if (value == null || value === "") return "";
  return String(value)
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const toSnakeCase = (value) => {
  if (value == null || value === "") return "";
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s_]/g, " ")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
};

export default function ConfigForm() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    key: "",
    // label: "",
    value: "",
    description: "",
    group: "",
  });

  const queryParams = new URLSearchParams(location.search);
  const configId = queryParams.get("configId");
  const isEditMode = Boolean(configId);

  const fieldSx = {
    "& .MuiInputBase-root": {
      display: "flex !important",
      placeItems: "unset !important",
      alignItems: "center !important",
      justifyContent: "flex-start !important",
      width: "100% !important",
    },
    "& .MuiInputBase-input": {
      width: "100% !important",
      textAlign: "left !important",
    },
    "& .MuiInputBase-inputMultiline": {
      width: "100% !important",
      textAlign: "left !important",
      display: "block !important",
    },
    "& textarea": {
      width: "100% !important",
      textAlign: "left !important",
    },
  };

  const numberFieldSx = {
    ...fieldSx,
    "& input[type=number]": {
      MozAppearance: "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  };

  const selectSx = {
    "& .MuiInputBase-root": {
      display: "flex !important",
      placeItems: "unset !important",
      alignItems: "center !important",
      justifyContent: "flex-start !important",
      width: "100% !important",
    },
    "& .MuiSelect-select": {
      textAlign: "left !important",
      width: "100% !important",
    },
  };

  const getConfigDetails = async () => {
    if (!configId) return;

    try {
      setIsLoading(true);
      const res = await api.getGamificationConfigDetails({ configId });

      if (res.status === 1) {
        const data = res.data;
        setFormData({
          key: toHumanReadable(data?.key),
        //   label: toHumanReadable(data?.label),
          value: data?.value ?? "",
          group: data?.group,
          description: data?.description ?? "",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (configId) getConfigDetails();
  }, [configId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      const payload = {
        key: toSnakeCase(formData.key),
        label: toSnakeCase(formData.label || formData.key),
        description: formData.description,
        value: formData.value,
        group: formData.group,
      };

      let res;

      if (isEditMode) {
        res = await api.updateGamificationConfig({
          configId,
          ...payload,
        });
      } else {
        res = await api.createConfig(payload);
      }

      if (res.status === 1) {
        navigate("/rules");
      } else {
        alert(res.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <SoftBox display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </SoftBox>
    );
  }

  return (
    <SoftBox py={3} display="flex" justifyContent="center">
      <SoftBox
        sx={{
          width: "100%",
          maxWidth: "850px",
          mx: "auto",
        }}
      >
        <Card sx={{ border: "1px solid #f0f2f5", width: "100%" }}>
          <SoftBox p={3}>
            
            {/* HEADER */}
            <SoftBox mb={4}>
              <SoftTypography variant="h6" fontWeight="bold">
                {isEditMode ? "Edit Configuration" : "Create Configuration"}
              </SoftTypography>
              <SoftTypography variant="caption" color="text">
                Use readable names in the form. We automatically save them in
                backend format (snake_case).
              </SoftTypography>
            </SoftBox>

            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              {/* GROUP */}
              <Box mb={3}>
                <SoftTypography
                  variant="button"
                  fontWeight="medium"
                  mb={1}
                  display="block"
                >
                  Group
                </SoftTypography>
                <FormControl fullWidth sx={selectSx}>
                  <Select
                    name="group"
                    value={formData.group}
                    onChange={handleChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select config group
                    </MenuItem>
                    {GROUP_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* NAME / KEY */}
              <Box mb={3}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Name
                </SoftTypography>
                <TextField
                  fullWidth
                  sx={fieldSx}
                  name="key"
                  value={formData.key}
                  onChange={handleChange}
                  placeholder="e.g. Session Start"
                  
                  // helperText={`Will save as: ${
                  //   toSnakeCase(formData.key) || "session_start"
                  // }`}

                />
              </Box>

              {/* LABEL + VALUE */}
              <Box display="flex" gap={3} mb={3}>
                {/* <Box flex={1}>
                  <SoftTypography variant="button" fontWeight="medium" mb={1}>
                    Label
                  </SoftTypography>
                  <TextField
                    fullWidth
                    sx={fieldSx}
                    name="label"
                    value={formData.label}
                    onChange={handleChange}
                    placeholder="e.g. Session End Case"
                    helperText={`Will save as: ${
                      toSnakeCase(formData.label || formData.key) ||
                      "session_end_case"
                    }`}
                  />
                </Box> */}
                <Box flex={1}>
                  <SoftTypography variant="button" fontWeight="medium" mb={1}>
                    Value
                  </SoftTypography>
                  <TextField
                    fullWidth
                    name="value"
                    value={formData.value}
                    // onChange={handleChange}
                    placeholder="e.g. 20"
                    helperText="Points / threshold value for this config"
                    onChange={(e) => {
                        const val = e.target.value;

                        // allow only digits (0–9)
                        if (val === "" || /^[0-9]+$/.test(val)) {
                        handleChange({
                            target: {
                                name: "value",
                                value: val,
                            },
                        });
                        }
                    }}
                    onKeyDown={(e) => {
                        // block everything except digits
                        if (
                        !/[0-9]/.test(e.key) &&
                        ![
                            "Backspace",
                            "Delete",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "ArrowUp",     // ✅ allow
                            "ArrowDown",
                        ].includes(e.key)
                        ) {
                        e.preventDefault();
                        }
                    }}
                    inputProps={{
                        inputMode: "numeric", // mobile numeric keyboard
                        pattern: "[0-9]*",
                    }}
                    sx={numberFieldSx}
                  />
                </Box>
              </Box>

              {/* DESCRIPTION */}
              <Box mb={4}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Description
                </SoftTypography>
                <TextField
                  fullWidth
                  sx={fieldSx}
                  multiline
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Short description..."
                />
              </Box>

              {/* SUBMIT */}
              <Box display="flex" justifyContent="flex-end">
                <SoftButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  disabled={isSubmitting}
                >
                  {isSubmitting && (
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                  )}
                  {isSubmitting
                    ? isEditMode
                      ? "Updating..."
                      : "Saving..."
                    : isEditMode
                    ? "Update Config"
                    : "Create Config"}
                </SoftButton>
              </Box>

            </form>
          </SoftBox>
        </Card>
      </SoftBox>
    </SoftBox>
  );
}